import { useEffect, useState } from "react";
import useAPI from "hooks/useAPI";
import { FormInput } from "components/Form/FormInputs/FormInput";
import formScss from "components/Form/FormWrapper.module.scss";
import selectStyles from "components/Form/FormInputs/FormInput.module.scss";

/**
 * A single "select" field on a delivery option form (e.g. choosing a store for pick-in-store).
 * Its options are not static - they're fetched from the backend's delivery-option-form-ddl
 * endpoint (a FETCH-verb endpoint), so it can't reuse CreateForm's GET-based fillFromApi.
 */
const DeliveryOptionSelectField = ({ field, value, onChange }) => {
    const api = useAPI();
    const [options, setOptions] = useState([]);

    useEffect(() => {
        let active = true;

        if (field.fillFromApi) {
            api.fetch(field.fillFromApi, { selected_data: [], field_options: {} }).then((res) => {
                if (active) {
                    setOptions(res?.values ?? []);
                }
            });
        }

        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [field.fillFromApi]);

    return (
        <div className={formScss.input}>
            <label htmlFor={field.prop_name} className={field.required ? formScss.required : ""}>
                {field.field_name}
            </label>
            <select
                className={selectStyles.select}
                id={field.prop_name}
                value={value?.id ?? ""}
                onChange={(event) => {
                    const selected = options.find((option) => String(option.id) === event.target.value) ?? null;
                    onChange(selected);
                }}
            >
                <option value="">-</option>
                {options.map((option) => (
                    <option key={option.id} value={option.id}>
                        {option.name}
                    </option>
                ))}
            </select>
        </div>
    );
};

/**
 * Renders the dynamic form fields for whichever delivery method is currently selected
 * (e.g. store picker for "in_store_pickup", courier/pickup details for "client_delivery").
 * Plain "delivery_to_address" has no such fields and this renders nothing.
 *
 * @param {Array} fields Field definitions from FETCH /checkout/delivery-option-form/{method}.
 * @param {{}} values Current prop_name => value map for the selected method.
 * @param {function} setValues Setter for `values`.
 */
const DeliveryOptionForm = ({ fields = [], values = {}, setValues }) => {
    if (!fields.length) {
        return null;
    }

    return (
        <>
            {fields.map((field) => {
                const value = values[field.prop_name];
                const onChange = (newValue) => setValues((prev) => ({ ...prev, [field.prop_name]: newValue }));

                if (field.input_type === "select") {
                    return <DeliveryOptionSelectField key={field.prop_name} field={field} value={value} onChange={onChange} />;
                }

                return (
                    <FormInput
                        key={field.prop_name}
                        label={field.field_name}
                        data={{ [field.prop_name]: value ?? "" }}
                        setData={(updater) => {
                            const updated = typeof updater === "function" ? updater({ [field.prop_name]: value ?? "" }) : updater;
                            onChange(updated[field.prop_name]);
                        }}
                        field={field.prop_name}
                        type={field.input_type === "date" || field.input_type === "time" ? field.input_type : "text"}
                        required={field.required}
                    />
                );
            })}
        </>
    );
};

export default DeliveryOptionForm;
